import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TableGameConfig, Category, TablePlayer } from '../../types/tableGame';
import { useTableGamePersistence } from '../../hooks/useTableGamePersistence';
import { TableGameService } from '../../services/tableGameService';
import { SEOMeta } from '../../templates/SEOMeta';
import PlayerCountSelector from './PlayerCountSelector';
import PlayerNamesInput from './PlayerNamesInput';
import WordModeSelector from './WordModeSelector';
import ImpostorModeSelector from './ImpostorModeSelector';
import InfoButton from './InfoButton';
import { logger } from '../../utils/logger';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useLocalizedNavigate } from '../../i18n/hooks';
import RecommendedSection from '../RecommendedSection';

const TableSetup: React.FC = () => {
  const { t } = useTranslation();
  usePageTitle(t('tableSetup.pageTitle'));
  
  const navigate = useLocalizedNavigate();
  const { config, saveConfig, initializeConfig } = useTableGamePersistence();
  
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize config if not exists
  useEffect(() => {
    if (!config) {
      initializeConfig();
    }
  }, [config, initializeConfig]);

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await TableGameService.getCategories();
        setCategories(cats);
      } catch (error) {
        logger.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, []);

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-blue-50 dark:bg-gray-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-300">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const handlePlayerCountChange = (count: number) => {
    const currentPlayers = config.players;
    let newPlayers: TablePlayer[];

    if (count > currentPlayers.length) {
      // Add new players
      newPlayers = [
        ...currentPlayers,
        ...Array.from({ length: count - currentPlayers.length }, (_, i) => ({
          id: crypto.randomUUID(),
          name: t('tableSetup.playerNames.defaultName', { index: currentPlayers.length + i + 1 }),
          order: currentPlayers.length + i
        }))
      ];
    } else {
      // Remove players
      newPlayers = currentPlayers.slice(0, count);
    }

    saveConfig({
      playerCount: count,
      players: newPlayers
    });
  };

  const handleUpdatePlayer = (playerId: string, name: string) => {
    const updatedPlayers = config.players.map(p =>
      p.id === playerId ? { ...p, name } : p
    );
    saveConfig({ players: updatedPlayers });
  };

  const handleStartGame = async () => {
    setError(null);
    setIsLoading(true);

    try {
      // Validate configuration
      if (config.wordMode === 'custom' && !config.customWord?.trim()) {
        setError(t('tableSetup.errors.customWordRequired'));
        setIsLoading(false);
        return;
      }

      if (config.wordMode === 'category' && !config.categoryId) {
        setError(t('tableSetup.errors.categoryRequired'));
        setIsLoading(false);
        return;
      }

      // Get the word based on mode
      let word: string;
      
      switch (config.wordMode) {
        case 'random':
          word = await TableGameService.getRandomWord();
          break;
        case 'custom':
          word = config.customWord!.trim();
          break;
        case 'category':
          word = await TableGameService.getRandomWordFromCategory(config.categoryId!);
          break;
        default:
          throw new Error('Invalid word mode');
      }

      // Assign roles to players
      // Crear un array con los índices de roles (impostores primero)
      const roles: Array<'impostor' | 'normal'> = [
        ...Array(config.impostorCount).fill('impostor'),
        ...Array(config.playerCount - config.impostorCount).fill('normal')
      ];
      
      // Barajar los roles usando el algoritmo Fisher-Yates para un shuffle verdaderamente aleatorio
      for (let i = roles.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [roles[i], roles[j]] = [roles[j], roles[i]];
      }
      
      // Asignar roles a los jugadores en su orden original
      const playersWithRoles = config.players.map((player, index) => ({
        ...player,
        role: roles[index],
        word: roles[index] === 'impostor' ? undefined : word
      }));

      // Save updated config with roles - Guardar directamente en localStorage para asegurar sincronización
      const updatedConfig = { ...config, players: playersWithRoles };
      try {
        localStorage.setItem('table_game_config', JSON.stringify(updatedConfig));
        logger.log('Config guardada con roles:', updatedConfig);
      } catch (error) {
        logger.error('Error guardando config:', error);
      }
      
      // Actualizar el estado
      saveConfig({ players: playersWithRoles });

      // Save audit (silent fail)
      try {
        await TableGameService.saveGameAudit({
          tableId: config.tableId,
          playerCount: config.playerCount,
          wordMode: config.wordMode,
          word,
          categoryId: config.categoryId,
          impostorMode: config.impostorMode,
          impostorCount: config.impostorCount
        });
      } catch (auditError) {
        logger.debug('Audit failed (expected):', auditError);
      }

      // Pequeño delay para asegurar que el estado se actualice
      await new Promise(resolve => setTimeout(resolve, 100));

      // Navigate to game page
      logger.log('Navegando a juego con tableId:', config.tableId);
      navigate(`/table/${config.tableId}/play`);
      
    } catch (error) {
      logger.error('Error starting game:', error);
      setError(t('tableSetup.errors.startFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEOMeta
        title={t('tableSetup.seo.title')}
        description={t('tableSetup.seo.description')}
        canonical="https://el-impostor.com/table"
      />
      <div className="min-h-screen bg-gradient-to-br from-[#E1ECFE] via-[#D1E3FE] to-[#C1D9FE] dark:from-gray-950 dark:via-gray-950 dark:to-gray-950 p-4 py-8">
      <div className="max-w-4xl mx-auto">
        <RecommendedSection position="top" />
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-2">
            <h1 className="text-4xl font-bold text-gray-800">
              {t('tableSetup.header.title')}
            </h1>
            <InfoButton
              title={t('tableSetup.header.infoTitle')}
              content={t('tableSetup.header.infoContent')}
            />
          </div>
          <p className="text-gray-600 dark:text-gray-300 text-lg">
            {t('tableSetup.header.subtitle')}
          </p>
        </div>

        {/* Main Form Card */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-6 md:p-8 mb-6">
          {/* Player Count */}
          <PlayerCountSelector
            playerCount={config.playerCount}
            onChange={handlePlayerCountChange}
            disabled={isLoading}
          />

          <div className="border-t border-gray-200 dark:border-gray-800 my-6"></div>

          {/* Player Names */}
          <PlayerNamesInput
            players={config.players}
            onUpdatePlayer={handleUpdatePlayer}
            disabled={isLoading}
          />

          <div className="border-t border-gray-200 dark:border-gray-800 my-6"></div>

          {/* Word Mode */}
          <WordModeSelector
            wordMode={config.wordMode}
            customWord={config.customWord}
            categoryId={config.categoryId}
            categories={categories}
            onModeChange={(mode) => saveConfig({ wordMode: mode })}
            onCustomWordChange={(word) => saveConfig({ customWord: word })}
            onCategoryChange={(id) => saveConfig({ categoryId: id })}
            disabled={isLoading}
          />

          <div className="border-t border-gray-200 dark:border-gray-800 my-6"></div>

          {/* Impostor Mode */}
          <ImpostorModeSelector
            impostorMode={config.impostorMode}
            impostorCount={config.impostorCount}
            playerCount={config.playerCount}
            onModeChange={(mode) => saveConfig({ impostorMode: mode })}
            onCountChange={(count) => saveConfig({ impostorCount: count })}
            disabled={isLoading}
          />

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-950/30 border-2 border-red-200 dark:border-red-900 rounded-lg">
              <p className="text-red-700 dark:text-red-200 text-sm font-medium">{error}</p>
            </div>
          )}

          {/* Start Button */}
          <button
            onClick={handleStartGame}
            disabled={isLoading}
            className={`
              w-full py-4 px-6 rounded-xl font-bold text-lg
              transition-all duration-200 transform
              ${isLoading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 hover:scale-105 shadow-lg hover:shadow-xl'
              }
              text-white
            `}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('tableSetup.actions.starting')}
              </span>
            ) : (
              t('tableSetup.actions.start')
            )}
          </button>
        </div>

        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          disabled={isLoading}
          className="w-full py-3 px-6 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-100 font-medium rounded-xl 
                     shadow-md transition-all duration-200"
        >
          {t('tableSetup.actions.backHome')}
        </button>
        <RecommendedSection position="bottom" />
      </div>
    </div>
    </>
  );
};

export default TableSetup;
